import { useId } from "react";
import {
  Controller,
  type Control,
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from "react-hook-form";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FormFieldProps<TFieldValues extends FieldValues> {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  label?: string;
  /** Shows a red asterisk next to the label */
  required?: boolean;
  helperText?: string;
  className?: string;
  /**
   * Render function for the actual input.
   * Receives the RHF field props, fieldState, and a stable id to wire to htmlFor.
   */
  children: (
    field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>,
    fieldState: ControllerFieldState,
    id: string,
  ) => React.ReactNode;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function FormField<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  required,
  helperText,
  className,
  children,
}: FormFieldProps<TFieldValues>) {
  const autoId = useId();
  const fieldId = `ff-${autoId}-${name}`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const errorMsg = fieldState.error?.message;

        return (
          <div className={cn("flex flex-col gap-1.5", className)}>
            {/* Label */}
            {label && (
              <label
                htmlFor={fieldId}
                className="text-sm font-medium text-content-primary leading-none"
              >
                {label}
                {required && (
                  <span className="text-red-500 ml-0.5" aria-hidden="true">
                    *
                  </span>
                )}
              </label>
            )}

            {/* Input */}
            {children(field, fieldState, fieldId)}

            {/* Animated error message */}
            <div
              role={errorMsg ? "alert" : undefined}
              aria-live="polite"
              className={cn(
                "overflow-hidden transition-all duration-200 ease-out",
                errorMsg ? "max-h-10 opacity-100" : "max-h-0 opacity-0",
              )}
            >
              <p className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1 leading-snug">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="shrink-0">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                {errorMsg}
              </p>
            </div>

            {/* Helper text — hidden when error is present */}
            {helperText && !errorMsg && (
              <p className="text-xs text-content-tertiary leading-snug">{helperText}</p>
            )}
          </div>
        );
      }}
    />
  );
}

FormField.displayName = "FormField";
